import { Check } from '@tunasong/icons'
import { useElementTypeMedia } from '@tunasong/plugin-lib'
import type { Entity, EntityType } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, HTMLAttributes } from 'react'
import { getTypeCounts, entityTypeLabel } from './sort-filter-util.js'
import styles from './type-filter-chips.module.css'

export interface TypeFilterChipsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  entities: Pick<Entity, 'type'>[]
  /** Selected types. Empty means all. */
  value: EntityType[]
  onChange(types: EntityType[]): void
}

const TypeIcon: FC<{ type: EntityType }> = ({ type }) => {
  const { icon: Icon } = useElementTypeMedia(type)
  return <Icon size={16} aria-hidden="true" />
}

interface ChipProps {
  label: string
  count: number
  selected: boolean
  icon?: EntityType
  onClick(): void
}

const Chip: FC<ChipProps> = ({ label, count, selected, icon, onClick }) => (
  <button
    type="button"
    className={cn(styles.chip, selected && styles.selected)}
    aria-pressed={selected}
    aria-label={`${label}, ${count}`}
    onClick={onClick}
  >
    {selected ? <Check size={14} aria-hidden="true" /> : icon ? <TypeIcon type={icon} /> : null}
    {label}
    <span className={styles.count}>{count}</span>
  </button>
)

/** Multi-select chips, one per entity type present, to filter a list of entities by type */
export const TypeFilterChips: FC<TypeFilterChipsProps> = props => {
  const { entities, value, onChange, className, ...restProps } = props

  const typeCounts = getTypeCounts(entities, value)

  const handleToggle = (type: EntityType) => () =>
    onChange(value.includes(type) ? value.filter(t => t !== type) : [...value, type])

  return (
    <div role="group" aria-label="Filter by type" className={cn(styles.root, className)} {...restProps}>
      <Chip label="All" count={entities.length} selected={value.length === 0} onClick={() => onChange([])} />
      {typeCounts.map(({ type, count }) => (
        <Chip
          key={type}
          label={entityTypeLabel(type)}
          count={count}
          icon={type}
          selected={value.includes(type)}
          onClick={handleToggle(type)}
        />
      ))}
    </div>
  )
}

export default TypeFilterChips
